// src/views/admin/PurchaseOrderPage.js
// Enhanced Production-Ready Purchase Order Management Page
// Features: Advanced debugging, modern UI/UX, filtering, sorting, bulk actions, statistics
// ENHANCEMENT: Modern gradient themes, Framer Motion animations, comprehensive functionality

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  useColorModeValue,
  Spinner,
  Text,
  Flex,
  Badge,
  Icon,
  Heading,
  HStack,
  VStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  IconButton,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Checkbox,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  Divider,
  Tag,
  TagLabel,
  TagLeftIcon,
  Alert,
  AlertIcon,
  AlertDescription,
  Progress,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerFooter,
  useDisclosure,
  Grid,
  FormControl,
  FormLabel,
  Switch,
  Skeleton,
  SkeletonText,
  Center,
  useBreakpointValue,
  TableContainer,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Tabs, TabList, TabPanels, Tab, TabPanel,
} from '@chakra-ui/react';
import {
  MdAdd,
  MdRefresh,
  MdSearch,
  MdFilterList,
  MdMoreVert,
  MdDownload,
  MdUpload,
  MdBugReport,
  MdCheckCircle,
  MdCancel,
  MdWarning,
  MdInfo,
  MdShoppingCart,
  MdLocalShipping,
  MdInventory,
  MdAttachMoney,
  MdDateRange,
  MdSort,
  MdSelectAll,
  MdDelete,
  MdEdit,
  MdVisibility,
  MdPrint,
  MdEmail,
  MdHistory,
  MdTrendingUp,
  MdTrendingDown,
  MdAutorenew,
  MdArrowUpward,
  MdArrowDownward,
  MdFirstPage,
  MdLastPage,
  MdChevronLeft,
  MdChevronRight,
  MdReceipt,
  MdPending,
  MdDone,
  MdClose,
  MdAccountBalanceWallet,
  MdAutoGraph,
} from 'react-icons/md';

// Framer Motion for animations
import { motion, AnimatePresence } from 'framer-motion';

import purchaseOrderService from '../../services/purchaseOrderService';
import PurchaseOrderDetailModal from '../../components/inventory/PurchaseOrderDetailModal';
import PurchaseOrderCreateModal from '../../components/inventory/PurchaseOrderCreateModal';
import POJustificationModal from '../../components/inventory/POJustificationModal';
import { FaRobot } from 'react-icons/fa';
import PurchaseOrderManager from '../../components/inventory/PurchaseOrderManager';
import PredictiveReorderPanel from '../../components/inventory/PredictiveReorderPanel';

// ============================================================================
// MOTION COMPONENTS
// ============================================================================
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionTr = motion(Tr);

// ============================================================================
// DEBUG CONFIGURATION & LOGGER
// ============================================================================
const DEBUG_CONFIG = {
  enabled: process.env.NODE_ENV === 'development',
  logLevel: 'all',
  logToConsole: true,
  logNetwork: true,
  logPerformance: true,
};

class DebugLogger {
  constructor(componentName) {
    this.componentName = componentName;
    this.logs = [];
    this.maxLogs = 500;
    this.enabled = DEBUG_CONFIG.enabled;
    this.listeners = [];
  }

  log(level, message, data = null) {
    if (!this.enabled) return;

    const timestamp = new Date().toISOString();
    let logData = data;

    if (data instanceof Error) {
      logData = {
        message: data.message,
        stack: data.stack,
        name: data.name,
      };
    }

    const logEntry = {
      timestamp,
      level,
      message,
      data: logData,
      component: this.componentName,
    };

    this.logs.push(logEntry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    this.listeners.forEach(listener => listener(logEntry));

    if (this.enabled && DEBUG_CONFIG.logToConsole) {
      const prefix = `[${this.componentName}][${level.toUpperCase()}]`;
      const style = this.getConsoleStyle(level);
      
      if (logData) {
        console.log(`%c${prefix} ${message}`, style, logData);
      } else {
        console.log(`%c${prefix} ${message}`, style);
      }
    }
  }

  getConsoleStyle(level) {
    const styles = {
      debug: 'color: #888; font-size: 11px',
      info: 'color: #2196F3; font-weight: bold',
      warn: 'color: #FF9800; font-weight: bold',
      error: 'color: #F44336; font-weight: bold',
      success: 'color: #4CAF50; font-weight: bold',
      network: 'color: #9C27B0; font-weight: bold',
    };
    return styles[level] || styles.info;
  }

  debug(message, data) { this.log('debug', message, data); }
  info(message, data) { this.log('info', message, data); }
  warn(message, data) { this.log('warn', message, data); }
  error(message, data) { this.log('error', message, data); }
  success(message, data) { this.log('success', message, data); }
  network(message, data) { this.log('network', message, data); }

  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  getLogs(level = null) {
    if (level) {
      return this.logs.filter(log => log.level === level);
    }
    return [...this.logs];
  }

  clear() {
    this.logs = [];
  }

  export() {
    return JSON.stringify(this.logs, null, 2);
  }
}

// ============================================================================
// PERFORMANCE MONITOR
// ============================================================================
class PerformanceMonitor {
  constructor() {
    this.metrics = {};
    this.enabled = DEBUG_CONFIG.enabled;
  }

  startTimer(label) {
    if (!this.enabled) return;
    this.metrics[label] = { start: performance.now() };
  }

  endTimer(label) {
    if (!this.enabled) return null;
    if (!this.metrics[label]) return null;
    
    const duration = performance.now() - this.metrics[label].start;
    this.metrics[label].duration = duration;
    this.metrics[label].end = performance.now();
    
    return duration;
  }

  getMetric(label) {
    return this.metrics[label];
  }

  getAllMetrics() {
    return { ...this.metrics };
  }

  clear() {
    this.metrics = {};
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount || 0);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatRelativeDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
};

const getStatusConfig = (status) => {
  const configs = {
    'Pending': { color: 'yellow', icon: MdPending, label: 'Pending' },
    'Ordered': { color: 'blue', icon: MdShoppingCart, label: 'Ordered' },
    'Shipped': { color: 'cyan', icon: MdLocalShipping, label: 'Shipped' },
    'Received': { color: 'green', icon: MdDone, label: 'Received' },
    'Cancelled': { color: 'red', icon: MdCancel, label: 'Cancelled' },
    'Partial': { color: 'orange', icon: MdWarning, label: 'Partial' },
  };
  return configs[status] || { color: 'gray', icon: MdInfo, label: status };
};

// ============================================================================
// ENHANCED STAT CARD COMPONENT
// ============================================================================
const EnhancedStatCard = ({ label, value, icon: IconComponent, color = 'blue', helpText, isLoading, trend, prefix = '' }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const valueColor = useColorModeValue('gray.800', 'white');
  const iconBg = useColorModeValue(`${color}.50`, `${color}.900`);
  const iconColor = useColorModeValue(`${color}.500`, `${color}.300`);
  const gradientBg = useColorModeValue(
    `linear(to-br, ${color}.50, white)`,
    `linear(to-br, ${color}.900, gray.800)`
  );

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, boxShadow: 'lg' }}
      transition={{ duration: 0.2 }}
    >
      <Box
        bg={cardBg}
        bgGradient={gradientBg}
        p={6}
        borderRadius="2xl"
        borderWidth="1px"
        borderColor={borderColor}
        boxShadow="md"
        position="relative"
        overflow="hidden"
        _hover={{
          borderColor: `${color}.500`,
        }}
        transition="all 0.3s"
      >
        <Box
          position="absolute"
          top="-20px"
          right="-20px"
          width="100px"
          height="100px"
          bg={iconBg}
          opacity={0.2}
          borderRadius="full"
          filter="blur(20px)"
        />

        <Stat position="relative" zIndex={1}>
          <Flex justify="space-between" align="flex-start" mb={2}>
            <Box flex={1}>
              <StatLabel color={labelColor} fontSize="sm" fontWeight="600" mb={2}>
                {label}
              </StatLabel>
              {isLoading ? (
                <Skeleton height="40px" width="80px" />
              ) : (
                <StatNumber fontSize="4xl" fontWeight="bold" color={valueColor} lineHeight="1">
                  {prefix}{value}
                </StatNumber>
              )}
              {helpText && (
                <StatHelpText fontSize="xs" color={labelColor} mt={2}>
                  {trend !== undefined && (
                    <StatArrow type={trend > 0 ? 'increase' : 'decrease'} />
                  )}
                  {helpText}
                </StatHelpText>
              )}
            </Box>
            <Box
              bg={iconBg}
              p={3}
              borderRadius="xl"
              boxShadow="md"
            >
              <Icon as={IconComponent} boxSize={8} color={iconColor} />
            </Box>
          </Flex>
        </Stat>
      </Box>
    </MotionBox>
  );
};

// ============================================================================
// ANIMATED STATUS BADGE
// ============================================================================
const AnimatedStatusBadge = ({ status }) => {
  const config = getStatusConfig(status);
  
  return (
    <MotionBox
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ scale: 1.05 }}
    >
      <Tag
        size="md"
        colorScheme={config.color}
        borderRadius="full"
        px={3}
        py={1}
      >
        <TagLeftIcon as={config.icon} boxSize={3} />
        <TagLabel fontWeight="600" fontSize="xs">
          {config.label}
        </TagLabel>
      </Tag>
    </MotionBox>
  );
};

// ============================================================================
// TABLE SKELETON LOADER
// ============================================================================
const TableSkeleton = ({ rows = 5 }) => {
  return (
    <>
      {[...Array(rows)].map((_, index) => (
        <Tr key={index}>
          <Td><Skeleton height="20px" width="30px" /></Td>
          <Td><Skeleton height="20px" width="60px" /></Td>
          <Td><Skeleton height="20px" width="120px" /></Td>
          <Td><Skeleton height="20px" width="80px" /></Td>
          <Td><Skeleton height="20px" width="70px" /></Td>
          <Td><Skeleton height="20px" width="80px" /></Td>
          <Td><Skeleton height="30px" width="100px" /></Td>
        </Tr>
      ))}
    </>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const PurchaseOrderPage = () => {
  const [tabIndex, setTabIndex] = useState(0); 
  // --- NEW: STATE TO HOLD THE AI DRAFT ITEMS ---
  const [aiDraftItems, setAiDraftItems] = useState(null);

  const logger = useRef(new DebugLogger('PurchaseOrderPage')).current;
  const perfMonitor = useRef(new PerformanceMonitor()).current;

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [aiTargetPO, setAiTargetPO] = useState(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(60);
  
  const [debugLogs, setDebugLogs] = useState([]);
  const [debugFilter, setDebugFilter] = useState('all');

  const [statistics, setStatistics] = useState({
    total: 0, pending: 0, ordered: 0, shipped: 0,
    received: 0, cancelled: 0, totalValue: 0, avgValue: 0,
  });

  const isMounted = useRef(true);
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { isOpen: isDebugDrawerOpen, onOpen: onDebugDrawerOpen, onClose: onDebugDrawerClose } = useDisclosure();

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const headingColor = useColorModeValue('gray.800', 'white');
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
  const inputBg = useColorModeValue('white', 'gray.700');
  const tableBg = useColorModeValue('white', 'gray.800');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.900');
  const tableHoverBg = useColorModeValue('blue.50', 'blue.900');
  const tableStripedBg = useColorModeValue('gray.50', 'gray.750');
  const debugRowBg = useColorModeValue('gray.100', 'gray.800');
  const headerGradient = useColorModeValue('linear(to-r, blue.500, teal.500)', 'linear(to-r, blue.400, teal.400)');

  const fetchPOs = useCallback(async (showLoadingState = true) => {
    perfMonitor.startTimer('fetchPOs');
    logger.network('Fetching purchase orders from API');
    
    if (showLoadingState) setIsLoading(true);
    else setIsRefreshing(true);
    setError(null);

    try {
      const data = await purchaseOrderService.getAllPOs();
      if (!isMounted.current) return;
      const ordersData = data || [];
      setPurchaseOrders(ordersData);
      const stats = calculateStatistics(ordersData);
      setStatistics(stats);
      logger.success(`Loaded ${ordersData.length} purchase orders`, stats);
      const duration = perfMonitor.endTimer('fetchPOs');
      logger.debug(`Fetch completed in ${duration?.toFixed(2)}ms`);
    } catch (err) {
      logger.error('Failed to fetch purchase orders', err);
      if (isMounted.current) {
        setError(err.message || 'Failed to fetch purchase orders');
        toast({ title: 'Error fetching purchase orders', description: err.message, status: 'error', duration: 5000, position: 'top-right' });
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  }, [toast, logger, perfMonitor]);

  const calculateStatistics = useCallback((orders) => {
    const stats = { total: orders.length, pending: 0, ordered: 0, shipped: 0, received: 0, cancelled: 0, totalValue: 0, avgValue: 0 };
    orders.forEach(order => {
      const status = order.status?.toLowerCase();
      if (stats.hasOwnProperty(status)) stats[status]++;
      stats.totalValue += parseFloat(order.total_amount || 0);
    });
    stats.avgValue = orders.length > 0 ? stats.totalValue / orders.length : 0;
    return stats;
  }, []);

  useEffect(() => {
    perfMonitor.startTimer('filterAndSort');
    let filtered = [...purchaseOrders];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(po => po.id?.toString().includes(query) || po.supplier_name?.toLowerCase().includes(query) || po.status?.toLowerCase().includes(query) || po.notes?.toLowerCase().includes(query));
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(po => po.status?.toLowerCase() === statusFilter.toLowerCase());
    }
    if (dateFilter !== 'all') {
      const now = new Date();
      const startOfDay = new Date(now.setHours(0, 0, 0, 0));
      filtered = filtered.filter(po => {
        const orderDate = new Date(po.order_date);
        switch (dateFilter) {
          case 'today': return orderDate >= startOfDay;
          case 'week': const weekAgo = new Date(startOfDay); weekAgo.setDate(weekAgo.getDate() - 7); return orderDate >= weekAgo;
          case 'month': const monthAgo = new Date(startOfDay); monthAgo.setMonth(monthAgo.getMonth() - 1); return orderDate >= monthAgo;
          case 'quarter': const quarterAgo = new Date(startOfDay); quarterAgo.setMonth(quarterAgo.getMonth() - 3); return orderDate >= quarterAgo;
          default: return true;
        }
      });
    }

    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'date': comparison = new Date(b.order_date) - new Date(a.order_date); break;
        case 'id': comparison = b.id - a.id; break;
        case 'supplier': comparison = (a.supplier_name || '').localeCompare(b.supplier_name || ''); break;
        case 'amount': comparison = (b.total_amount || 0) - (a.total_amount || 0); break;
        case 'status': comparison = (a.status || '').localeCompare(b.status || ''); break;
        default: comparison = 0;
      }
      return sortOrder === 'asc' ? -comparison : comparison;
    });

    setFilteredOrders(filtered);
    setCurrentPage(1); 
    
    const duration = perfMonitor.endTimer('filterAndSort');
    logger.debug(`Filter/sort completed in ${duration?.toFixed(2)}ms`, { total: purchaseOrders.length, filtered: filtered.length });
  }, [purchaseOrders, searchQuery, statusFilter, dateFilter, sortBy, sortOrder, logger, perfMonitor]);

  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredOrders.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredOrders, currentPage, itemsPerPage]);

  const totalPages = useMemo(() => Math.ceil(filteredOrders.length / itemsPerPage), [filteredOrders.length, itemsPerPage]);

  const handleSelectAll = useCallback((checked) => {
    setSelectAll(checked);
    if (checked) setSelectedIds(new Set(paginatedOrders.map(po => po.id)));
    else setSelectedIds(new Set());
  }, [paginatedOrders]);

  const handleSelectOne = useCallback((id, checked) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (checked) newSet.add(id);
      else newSet.delete(id);
      return newSet;
    });
  }, []);

  const handleBulkAction = useCallback(async (action) => {
    if (selectedIds.size === 0) {
      toast({ title: 'No items selected', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    switch (action) {
      case 'export':
        const selectedOrders = purchaseOrders.filter(po => selectedIds.has(po.id));
        const csvContent = convertToCSV(selectedOrders);
        downloadCSV(csvContent, `purchase-orders-${Date.now()}.csv`);
        toast({ title: 'Export successful', description: `Exported ${selectedIds.size} purchase orders`, status: 'success', duration: 3000 });
        break;
      case 'print':
        toast({ title: 'Printing', description: `Preparing ${selectedIds.size} orders for printing...`, status: 'info', duration: 3000 });
        break;
      case 'delete':
        if (window.confirm(`Are you sure you want to delete ${selectedIds.size} purchase orders?`)) {
          toast({ title: 'Delete initiated', description: `Deleting ${selectedIds.size} orders...`, status: 'info', duration: 3000 });
        }
        break;
      default: break;
    }
  }, [selectedIds, purchaseOrders, toast, logger]);

  const convertToCSV = (data) => {
    if (data.length === 0) return '';
    const headers = ['ID', 'Supplier', 'Order Date', 'Status', 'Total Amount', 'Notes'];
    const rows = data.map(po => [po.id, po.supplier_name, formatDate(po.order_date), po.status, po.total_amount || 0, po.notes || '']);
    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a'); link.href = url; link.download = filename; link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportAll = useCallback(() => {
    const csvContent = convertToCSV(filteredOrders);
    downloadCSV(csvContent, `all-purchase-orders-${Date.now()}.csv`);
    toast({ title: 'Export successful', description: `Exported ${filteredOrders.length} purchase orders`, status: 'success', duration: 3000 });
  }, [filteredOrders, toast]);

  const handleViewDetails = useCallback((po) => {
    setSelectedPO(po);
    setIsDetailModalOpen(true);
  }, []);

  const handleOpenAiJustification = useCallback((po) => {
    setAiTargetPO(po);
    setIsAiModalOpen(true);
  }, []);

  const handleSaveJustification = useCallback(async (justificationText) => {
    try {
      await purchaseOrderService.updatePONotes(aiTargetPO.id, justificationText);
      toast({ title: 'Justification Saved', description: 'The AI memo has been attached to the Purchase Order notes.', status: 'success', duration: 3000 });
      fetchPOs(false); 
    } catch (error) {
      toast({ title: 'Save Failed', status: 'error', duration: 3000 });
    }
  }, [aiTargetPO, fetchPOs, toast]);

  const handleRefresh = useCallback(async () => {
    await fetchPOs(false);
    toast({ title: 'Data refreshed', status: 'success', duration: 2000, position: 'bottom-right' });
  }, [fetchPOs, toast]);

  const handleSort = useCallback((column) => {
    if (sortBy === column) setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortBy(column); setSortOrder('desc'); }
  }, [sortBy]);

  useEffect(() => {
    const unsubscribe = logger.subscribe((logEntry) => setDebugLogs(prev => [...prev.slice(-99), logEntry]));
    return unsubscribe;
  }, [logger]);

  useEffect(() => {
    isMounted.current = true;
    fetchPOs();
    return () => { isMounted.current = false; };
  }, [fetchPOs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const intervalId = setInterval(() => fetchPOs(false), refreshInterval * 1000);
    return () => clearInterval(intervalId);
  }, [autoRefresh, refreshInterval, fetchPOs]);

  const filteredDebugLogs = useMemo(() => {
    if (debugFilter === 'all') return debugLogs;
    return debugLogs.filter(log => log.level === debugFilter);
  }, [debugLogs, debugFilter]);

  // --- DATA HANDOFF HANDLER ---
  const handleDraftPO = (draftItems) => {
    logger.info('AI generated draft items routing to Budget Manager', draftItems);
    setAiDraftItems(draftItems); // Store it in state
    setTabIndex(1);              // Force UI to switch to Budget Manager Tab
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <Box pt={{ base: '10px', md: '10px', xl: '10px' }} bg={bgColor} minH="100vh">
      <MotionFlex initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} justify="space-between" align="center" mb={6} wrap="wrap" gap={4} px={{ base: 2, md: 0 }}>
        <Box>
          <Flex align="center" gap={3} mb={2}>
            <MotionBox initial={{ scale: 0 }} animate={{ scale: 1, rotate: 360 }} transition={{ duration: 0.6, type: 'spring' }} bgGradient={headerGradient} p={3} borderRadius="xl" boxShadow="lg">
              <Icon as={MdShoppingCart} color="white" boxSize={7} />
            </MotionBox>
            <Box>
              <Heading color={headingColor} size={isMobile ? 'lg' : 'xl'} fontWeight="800" bgGradient={headerGradient} bgClip="text">
                Purchase Orders
              </Heading>
              <Text fontSize="sm" color={mutedTextColor} fontWeight="500">
                Create and manage orders to suppliers
              </Text>
            </Box>
          </Flex>
        </Box>
        
        <HStack spacing={3} flexWrap="wrap">
          {autoRefresh && (
            <MotionBox initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
              <Tag size="lg" colorScheme="green" borderRadius="full" px={4}>
                <Icon as={MdAutorenew} mr={2} /> Auto-refresh: {refreshInterval}s
              </Tag>
            </MotionBox>
          )}
          <Tooltip label="Refresh Data" hasArrow><MotionBox whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}><IconButton icon={<Icon as={MdRefresh} />} size="md" onClick={handleRefresh} isLoading={isRefreshing} variant="ghost" colorScheme="blue" borderRadius="full" /></MotionBox></Tooltip>
          <Tooltip label="Toggle Filters" hasArrow><MotionBox whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}><IconButton icon={<Icon as={MdFilterList} />} size="md" onClick={() => setShowFilters(!showFilters)} variant={showFilters ? 'solid' : 'ghost'} colorScheme="blue" borderRadius="full" /></MotionBox></Tooltip>
          {DEBUG_CONFIG.enabled && (
            <Tooltip label="Debug Panel" hasArrow><MotionBox whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}><IconButton icon={<Icon as={MdBugReport} />} size="md" onClick={onDebugDrawerOpen} variant="ghost" colorScheme="yellow" borderRadius="full" /></MotionBox></Tooltip>
          )}
          <Menu>
            <MenuButton as={IconButton} icon={<Icon as={MdMoreVert} />} size="md" variant="ghost" borderRadius="full" />
            <MenuList>
              <MenuItem icon={<Icon as={autoRefresh ? MdCheckCircle : MdAutorenew} color={autoRefresh ? 'green.500' : 'gray.500'} />} onClick={() => setAutoRefresh(!autoRefresh)}>{autoRefresh ? 'Disable' : 'Enable'} Auto-refresh</MenuItem>
              <MenuItem closeOnSelect={false}>
                <HStack w="full" justify="space-between">
                  <Text fontSize="sm">Interval:</Text>
                  <Select size="xs" value={refreshInterval} onChange={(e) => setRefreshInterval(Number(e.target.value))} w="80px"><option value={30}>30s</option><option value={60}>60s</option><option value={120}>2m</option><option value={300}>5m</option></Select>
                </HStack>
              </MenuItem>
              <MenuDivider />
              <MenuItem icon={<Icon as={MdDownload} />} onClick={handleExportAll}>Export All to CSV</MenuItem>
              <MenuItem icon={<Icon as={MdPrint} />} onClick={() => window.print()}>Print View</MenuItem>
            </MenuList>
          </Menu>
          <MotionBox whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button leftIcon={<Icon as={MdAdd} />} colorScheme="blue" size="md" onClick={() => setIsCreateModalOpen(true)} borderRadius="xl" fontWeight="600" boxShadow="md">Create New PO</Button>
          </MotionBox>
        </HStack>
      </MotionFlex>

      <Tabs variant="soft-rounded" colorScheme="blue" index={tabIndex} onChange={(index) => setTabIndex(index)} isLazy>
        <TabList mb={6} bg={useColorModeValue('gray.50', 'gray.800')} p={2} borderRadius="2xl" mx={{ base: 2, md: 0 }}>
          <Tab fontWeight="bold" borderRadius="xl"><Icon as={MdReceipt} mr={2}/> Order Ledger</Tab>
          <Tab fontWeight="bold" borderRadius="xl"><Icon as={MdAccountBalanceWallet} mr={2}/> Budget Manager</Tab>
          <Tab fontWeight="bold" borderRadius="xl" color="purple.500" _selected={{ color: 'white', bg: 'purple.500' }}><Icon as={MdAutoGraph} mr={2}/> Smart Reorder</Tab>
        </TabList>

        <TabPanels>
          {/* TAB 0: ORDER LEDGER */}
          <TabPanel p={0}>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={5} mb={6}>
              <EnhancedStatCard label="Total Orders" value={statistics.total} icon={MdReceipt} color="blue" helpText={`${filteredOrders.length} shown`} isLoading={isLoading} />
              <EnhancedStatCard label="Pending Orders" value={statistics.pending} icon={MdPending} color="yellow" helpText="Awaiting processing" isLoading={isLoading} />
              <EnhancedStatCard label="In Transit" value={statistics.shipped} icon={MdLocalShipping} color="cyan" helpText="Currently shipping" isLoading={isLoading} />
              <EnhancedStatCard label="Total Value" value={formatCurrency(statistics.totalValue)} icon={MdAttachMoney} color="green" helpText={`Avg: ${formatCurrency(statistics.avgValue)}`} isLoading={isLoading} />
            </SimpleGrid>

            <AnimatePresence>
              {showFilters && (
                <MotionBox initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} mb={6}>
                  <Flex direction={{ base: 'column', lg: 'row' }} gap={4} bg={cardBg} p={5} borderRadius="2xl" borderWidth="1px" borderColor={cardBorderColor} boxShadow="md">
                    <HStack spacing={3} flex={1} flexWrap="wrap">
                      <InputGroup size="md" maxW="280px"><InputLeftElement><Icon as={MdSearch} color="gray.400" /></InputLeftElement><Input placeholder="Search by ID, supplier..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} borderRadius="xl" bg={inputBg} /></InputGroup>
                      <Select size="md" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} maxW="160px" borderRadius="xl" bg={inputBg}><option value="all">All Status</option><option value="pending">Pending</option><option value="ordered">Ordered</option><option value="shipped">Shipped</option><option value="received">Received</option><option value="cancelled">Cancelled</option></Select>
                      <Select size="md" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} maxW="160px" borderRadius="xl" bg={inputBg}><option value="all">All Time</option><option value="today">Today</option><option value="week">This Week</option><option value="month">This Month</option><option value="quarter">This Quarter</option></Select>
                      <Select size="md" value={sortBy} onChange={(e) => setSortBy(e.target.value)} maxW="160px" borderRadius="xl" bg={inputBg}><option value="date">Sort by Date</option><option value="id">Sort by ID</option><option value="supplier">Sort by Supplier</option><option value="amount">Sort by Amount</option><option value="status">Sort by Status</option></Select>
                      <Tooltip label={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}><IconButton icon={<Icon as={sortOrder === 'asc' ? MdArrowUpward : MdArrowDownward} />} onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')} variant="outline" size="md" borderRadius="xl" /></Tooltip>
                    </HStack>
                    {selectedIds.size > 0 && (
                      <HStack spacing={2}>
                        <Tag colorScheme="blue" size="lg" borderRadius="full">{selectedIds.size} selected</Tag>
                        <Menu><MenuButton as={Button} size="sm" variant="outline" colorScheme="blue" borderRadius="xl">Bulk Actions</MenuButton><MenuList><MenuItem icon={<Icon as={MdDownload} />} onClick={() => handleBulkAction('export')}>Export Selected</MenuItem><MenuItem icon={<Icon as={MdPrint} />} onClick={() => handleBulkAction('print')}>Print Selected</MenuItem><MenuDivider /><MenuItem icon={<Icon as={MdDelete} />} color="red.500" onClick={() => handleBulkAction('delete')}>Delete Selected</MenuItem></MenuList></Menu>
                      </HStack>
                    )}
                  </Flex>
                </MotionBox>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {error && (
                <MotionBox initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} mb={6}>
                  <Alert status="error" borderRadius="xl" boxShadow="md"><AlertIcon /><AlertDescription flex={1}>{error}</AlertDescription><Button size="sm" onClick={() => fetchPOs()} ml={4}>Retry</Button></Alert>
                </MotionBox>
              )}
            </AnimatePresence>

            <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Box bg={cardBg} borderRadius="3xl" boxShadow="xl" borderWidth="2px" borderColor={cardBorderColor} overflow="hidden">
                <Box h="4px" bgGradient={headerGradient} />
                <TableContainer>
                  <Table variant="simple">
                    <Thead bg={tableHeaderBg}>
                      <Tr>
                        <Th borderColor={cardBorderColor} w="50px"><Checkbox isChecked={selectAll} onChange={(e) => handleSelectAll(e.target.checked)} colorScheme="blue" /></Th>
                        <Th borderColor={cardBorderColor} cursor="pointer" onClick={() => handleSort('id')} _hover={{ color: 'blue.500' }}><HStack spacing={1}><Text>PO ID</Text>{sortBy === 'id' && <Icon as={sortOrder === 'asc' ? MdArrowUpward : MdArrowDownward} boxSize={4} />}</HStack></Th>
                        <Th borderColor={cardBorderColor} cursor="pointer" onClick={() => handleSort('supplier')} _hover={{ color: 'blue.500' }}><HStack spacing={1}><Text>Supplier</Text>{sortBy === 'supplier' && <Icon as={sortOrder === 'asc' ? MdArrowUpward : MdArrowDownward} boxSize={4} />}</HStack></Th>
                        <Th borderColor={cardBorderColor} cursor="pointer" onClick={() => handleSort('date')} _hover={{ color: 'blue.500' }}><HStack spacing={1}><Text>Order Date</Text>{sortBy === 'date' && <Icon as={sortOrder === 'asc' ? MdArrowUpward : MdArrowDownward} boxSize={4} />}</HStack></Th>
                        <Th borderColor={cardBorderColor} cursor="pointer" onClick={() => handleSort('amount')} _hover={{ color: 'blue.500' }}><HStack spacing={1}><Text>Amount</Text>{sortBy === 'amount' && <Icon as={sortOrder === 'asc' ? MdArrowUpward : MdArrowDownward} boxSize={4} />}</HStack></Th>
                        <Th borderColor={cardBorderColor} cursor="pointer" onClick={() => handleSort('status')} _hover={{ color: 'blue.500' }}><HStack spacing={1}><Text>Status</Text>{sortBy === 'status' && <Icon as={sortOrder === 'asc' ? MdArrowUpward : MdArrowDownward} boxSize={4} />}</HStack></Th>
                        <Th borderColor={cardBorderColor}>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {isLoading ? (
                        <TableSkeleton rows={5} />
                      ) : paginatedOrders.length === 0 ? (
                        <Tr><Td colSpan={7} borderColor={cardBorderColor}><Center py={12}><VStack spacing={4}><MotionBox initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}><Box bg="gray.100" p={6} borderRadius="full"><Icon as={MdReceipt} boxSize={12} color="gray.400" /></Box></MotionBox><Heading size="md" color={mutedTextColor}>{purchaseOrders.length === 0 ? 'No Purchase Orders' : 'No Results Found'}</Heading><Text color={mutedTextColor} textAlign="center" maxW="sm">{purchaseOrders.length === 0 ? 'Create your first purchase order to get started.' : 'Try adjusting your search or filter criteria.'}</Text>{purchaseOrders.length === 0 ? (<Button leftIcon={<Icon as={MdAdd} />} colorScheme="blue" onClick={() => setIsCreateModalOpen(true)} borderRadius="xl">Create First PO</Button>) : (<Button variant="outline" onClick={() => { setSearchQuery(''); setStatusFilter('all'); setDateFilter('all'); }} borderRadius="xl">Clear Filters</Button>)}</VStack></Center></Td></Tr>
                      ) : (
                        <AnimatePresence>
                          {paginatedOrders.map((po, index) => (
                            <MotionTr key={po.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ delay: index * 0.03 }} _hover={{ bg: tableHoverBg }} cursor="pointer" onClick={() => handleViewDetails(po)} bg={index % 2 === 0 ? 'transparent' : tableStripedBg}>
                              <Td borderColor={cardBorderColor} onClick={(e) => e.stopPropagation()}><Checkbox isChecked={selectedIds.has(po.id)} onChange={(e) => handleSelectOne(po.id, e.target.checked)} colorScheme="blue" /></Td>
                              <Td borderColor={cardBorderColor}><HStack><Badge colorScheme="blue" borderRadius="full" px={2}>#{po.id}</Badge></HStack></Td>
                              <Td borderColor={cardBorderColor}><VStack align="start" spacing={0}><Text fontWeight="600" color={textColor}>{po.supplier_name}</Text>{po.supplier_email && <Text fontSize="xs" color={mutedTextColor}>{po.supplier_email}</Text>}</VStack></Td>
                              <Td borderColor={cardBorderColor}><VStack align="start" spacing={0}><Text fontWeight="500">{formatDate(po.order_date)}</Text><Text fontSize="xs" color={mutedTextColor}>{formatRelativeDate(po.order_date)}</Text></VStack></Td>
                              <Td borderColor={cardBorderColor}><Text fontWeight="700" color="green.500">{formatCurrency(po.total_amount)}</Text></Td>
                              <Td borderColor={cardBorderColor}><AnimatedStatusBadge status={po.status} /></Td>
                              <Td borderColor={cardBorderColor} onClick={(e) => e.stopPropagation()}>
                                <HStack spacing={2}>
                                  <Tooltip label="View Details" hasArrow><IconButton icon={<Icon as={MdVisibility} />} size="sm" variant="ghost" colorScheme="blue" borderRadius="full" onClick={() => handleViewDetails(po)} /></Tooltip>
                                  <Menu>
                                    <MenuButton as={IconButton} icon={<Icon as={MdMoreVert} />} size="sm" variant="ghost" borderRadius="full" />
                                    <MenuList>
                                      <MenuItem icon={<Icon as={MdEdit} />}>Edit Order</MenuItem>
                                      <MenuItem icon={<Icon as={MdPrint} />}>Print PO</MenuItem>
                                      <MenuItem icon={<Icon as={MdEmail} />}>Email Supplier</MenuItem>
                                      {po.status === 'Pending' && <MenuItem icon={<Icon as={FaRobot} color="purple.500" />} onClick={() => handleOpenAiJustification(po)}><Text color="purple.600" fontWeight="500">Generate AI Memo</Text></MenuItem>}
                                      <MenuItem icon={<Icon as={MdHistory} />}>View History</MenuItem>
                                      <MenuDivider />
                                      <MenuItem icon={<Icon as={MdCancel} />} color="red.500">Cancel Order</MenuItem>
                                    </MenuList>
                                  </Menu>
                                </HStack>
                              </Td>
                            </MotionTr>
                          ))}
                        </AnimatePresence>
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>

                {filteredOrders.length > 0 && (
                  <Flex justify="space-between" align="center" p={4} borderTopWidth="1px" borderColor={cardBorderColor} bg={tableHeaderBg} flexWrap="wrap" gap={4}>
                    <HStack spacing={4}>
                      <Text fontSize="sm" color={mutedTextColor}>Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredOrders.length)} of {filteredOrders.length} results</Text>
                      <HStack spacing={2}>
                        <Text fontSize="sm" color={mutedTextColor}>Per page:</Text>
                        <Select size="sm" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} w="70px" borderRadius="lg"><option value={5}>5</option><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option></Select>
                      </HStack>
                    </HStack>
                    <HStack spacing={2}>
                      <IconButton icon={<Icon as={MdFirstPage} />} size="sm" variant="outline" borderRadius="lg" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1} />
                      <IconButton icon={<Icon as={MdChevronLeft} />} size="sm" variant="outline" borderRadius="lg" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} isDisabled={currentPage === 1} />
                      <HStack spacing={1}>
                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                          let pageNum;
                          if (totalPages <= 5) pageNum = i + 1;
                          else if (currentPage <= 3) pageNum = i + 1;
                          else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                          else pageNum = currentPage - 2 + i;
                          return <Button key={pageNum} size="sm" variant={currentPage === pageNum ? 'solid' : 'ghost'} colorScheme={currentPage === pageNum ? 'blue' : 'gray'} borderRadius="lg" onClick={() => setCurrentPage(pageNum)} minW="36px">{pageNum}</Button>;
                        })}
                      </HStack>
                      <IconButton icon={<Icon as={MdChevronRight} />} size="sm" variant="outline" borderRadius="lg" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} isDisabled={currentPage === totalPages} />
                      <IconButton icon={<Icon as={MdLastPage} />} size="sm" variant="outline" borderRadius="lg" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages} />
                    </HStack>
                  </Flex>
                )}
              </Box>
            </MotionBox>
          </TabPanel>

          {/* TAB 1: BUDGET MANAGER */}
          <TabPanel p={0}>
            <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              {/* --- NEW: Pass the AI draft items down to the Manager --- */}
              <PurchaseOrderManager 
                 aiDraftItems={aiDraftItems} 
                 clearAiDraft={() => setAiDraftItems(null)} 
              />
            </MotionBox>
          </TabPanel>

          {/* TAB 2: SMART REORDER */}
          <TabPanel p={0}>
            <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <PredictiveReorderPanel onDraftPO={handleDraftPO} />
            </MotionBox>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* MODALS */}
      {isDetailModalOpen && selectedPO && <PurchaseOrderDetailModal isOpen={isDetailModalOpen} onClose={() => { setIsDetailModalOpen(false); setSelectedPO(null); }} poId={selectedPO.id} onStatusUpdate={fetchPOs} />}
      {isCreateModalOpen && <PurchaseOrderCreateModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSave={() => { setIsCreateModalOpen(false); fetchPOs(); }} />}
      {isAiModalOpen && aiTargetPO && <POJustificationModal isOpen={isAiModalOpen} onClose={() => { setIsAiModalOpen(false); setAiTargetPO(null); }} item={{ id: aiTargetPO.id, name: `Purchase Order #${aiTargetPO.id} Supplies`, supplier_name: aiTargetPO.supplier_name }} orderQuantity={1} onSaveJustification={handleSaveJustification} />}
      
      {/* DEBUG DRAWER */}
      <Drawer isOpen={isDebugDrawerOpen} placement="right" onClose={onDebugDrawerClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px"><HStack><Icon as={MdBugReport} color="yellow.500" /><Text>Debug Panel</Text></HStack></DrawerHeader>
          <DrawerBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Heading size="sm" mb={3}>System Statistics</Heading>
                <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                  <Text fontSize="sm" fontWeight="600">Total Orders:</Text><Text fontSize="sm">{statistics.total}</Text>
                  <Text fontSize="sm" fontWeight="600">Filtered:</Text><Text fontSize="sm">{filteredOrders.length}</Text>
                  <Text fontSize="sm" fontWeight="600">Current Page:</Text><Text fontSize="sm">{currentPage} / {totalPages}</Text>
                  <Text fontSize="sm" fontWeight="600">Items Per Page:</Text><Text fontSize="sm">{itemsPerPage}</Text>
                  <Text fontSize="sm" fontWeight="600">Selected:</Text><Text fontSize="sm">{selectedIds.size}</Text>
                  <Text fontSize="sm" fontWeight="600">Auto-refresh:</Text><Text fontSize="sm">{autoRefresh ? `${refreshInterval}s` : 'Off'}</Text>
                </Grid>
              </Box>
              <Divider />
              <Box>
                <Heading size="sm" mb={3}>Status Breakdown</Heading>
                <VStack spacing={2} align="stretch">
                  {['pending', 'ordered', 'shipped', 'received', 'cancelled'].map(status => (
                    <Flex key={status} justify="space-between" p={2} bg={debugRowBg} borderRadius="md">
                      <Badge colorScheme={getStatusConfig(status.charAt(0).toUpperCase() + status.slice(1)).color}>{status.toUpperCase()}</Badge>
                      <Text fontWeight="600">{statistics[status] || 0}</Text>
                    </Flex>
                  ))}
                </VStack>
              </Box>
              <Divider />
              <Box>
                <Flex justify="space-between" align="center" mb={3}>
                  <Heading size="sm">Debug Logs</Heading>
                  <HStack>
                    <Select size="sm" value={debugFilter} onChange={(e) => setDebugFilter(e.target.value)} maxW="100px" borderRadius="md"><option value="all">All</option><option value="debug">Debug</option><option value="info">Info</option><option value="warn">Warn</option><option value="error">Error</option><option value="success">Success</option><option value="network">Network</option></Select>
                    <Button size="sm" onClick={() => logger.clear()} colorScheme="red" variant="ghost">Clear</Button>
                  </HStack>
                </Flex>
                <VStack spacing={2} align="stretch" maxH="300px" overflowY="auto" bg={useColorModeValue('gray.50', 'gray.900')} p={3} borderRadius="lg" borderWidth="1px" borderColor={cardBorderColor}>
                  {filteredDebugLogs.slice(-50).reverse().map((log, index) => (
                    <Box key={index} p={2} bg={debugRowBg} borderRadius="md" fontSize="xs" fontFamily="mono">
                      <Flex justify="space-between" mb={1}>
                        <Badge colorScheme={log.level === 'error' ? 'red' : log.level === 'warn' ? 'orange' : log.level === 'success' ? 'green' : log.level === 'network' ? 'purple' : log.level === 'info' ? 'blue' : 'gray'} fontSize="10px">{log.level.toUpperCase()}</Badge>
                        <Text color="gray.500" fontSize="10px">{new Date(log.timestamp).toLocaleTimeString()}</Text>
                      </Flex>
                      <Text fontWeight="500">{log.message}</Text>
                      {log.data && <Text color="gray.500" fontSize="10px" mt={1}>{JSON.stringify(log.data).substring(0, 100)}...</Text>}
                    </Box>
                  ))}
                </VStack>
              </Box>
              <Divider />
              <Box>
                <Heading size="sm" mb={3}>Performance Metrics</Heading>
                <VStack spacing={2} align="stretch">
                  {Object.entries(perfMonitor.getAllMetrics()).map(([label, metric]) => (
                    <Flex key={label} justify="space-between" p={2} bg={debugRowBg} borderRadius="md"><Text fontSize="sm" fontWeight="500">{label}</Text><Badge colorScheme={metric.duration < 100 ? 'green' : metric.duration < 500 ? 'yellow' : 'red'}>{metric.duration?.toFixed(2)}ms</Badge></Flex>
                  ))}
                </VStack>
              </Box>
            </VStack>
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px">
            <Button size="sm" mr={3} onClick={() => { const logs = logger.export(); const blob = new Blob([logs], { type: 'application/json' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `po-debug-logs-${Date.now()}.json`; a.click(); }} leftIcon={<Icon as={MdDownload} />}>Export Logs</Button>
            <Button size="sm" onClick={onDebugDrawerClose} colorScheme="blue">Close</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default PurchaseOrderPage;